import DeleteIcon from "@mui/icons-material/Delete";
import {
  Autocomplete,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Tooltip,
} from "@mui/material";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import { useEffect, useState } from "react";
import { Division } from "../../../models/Division";
import { RequestToSend } from "../../../models/RequestToSend";
import { Section } from "../../../models/Section";
import { User } from "../../../models/User";
import {
  addRequestToSend,
  addRequestsToSend,
} from "../../../redux/features/appStateSlice";
import {
  planoAPI
} from "../../../redux/features/cache";
import { useAppDispatch, useAppSelector } from "../../../redux/store";

export interface ModificationSectionProps {
  section: Section;
  handleClose: any;
}

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

export const ModificationSection = ({
  section,
  handleClose,
}: ModificationSectionProps) => {
  const dispatch = useAppDispatch();
  const d_inv026 = useAppSelector((state) => state.appState.d_inv026);

  const [addAcheteur] = planoAPI.endpoints.addAcheteur.useMutation();
  const [deleteAcheteur] = planoAPI.endpoints.deleteAcheteur.useMutation();

  const [updateSection] = planoAPI.endpoints.updateSection.useMutation();
  const [deleteSection] = planoAPI.endpoints.deleteSection.useMutation();

  const { data: divisions = [] as Division[] } = planoAPI.endpoints.getDivisions.useQuery({
    treeView: false,
    includeSections: false,
    plano: false,
  });

  const { data: users = [] as User[] } = planoAPI.endpoints.getUsers.useQuery({
    fonction: "Acheteur",
    sort: "usr_nom",
    div_num: "",
  });

  const [division, setDivision] = useState<Division | null>(null);
  const [sectionName, setSectionName] = useState<string>(section.plans_nom);
  const [sectionIndex, setSectionIndex] = useState<number | null>(section.plans_ordre);
  const [dirty, setDirty] = useState<boolean>(false);
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const [buyerNames, setBuyerNames] = useState<string[]>(
    section.secAcheteur !== undefined
      ? section.secAcheteur.map((buyer) => buyer.usr_nom)
      : []
  );

  useEffect(() => {
    if (divisions.length > 0) {
      const selectedDivision = divisions.find(division => division.div_num === section.div_num) ?? null;
      setDivision(selectedDivision);
    }
  }, [divisions, section.div_num]);

  useEffect(() => {
    setDirty(false);
  }, [section]);

  const addedAcheteur = async (plans_seq: number | null, acht: string[]) => {
    let key = acht.map((e) => users.find((x) => x.usr_nom === e)?.usr_cd);

    let addTask = key.map((element) => {
      return new RequestToSend(
        `Ajout acheteur ${element}`,
        addAcheteur,
        () => { },
        {},
        { plans_seq: plans_seq, usr_cd: element },
        []
      );
    });
    dispatch(addRequestsToSend(addTask));
  };

  const deletedAcheteur = async (plans_seq: number | null, acht: string[]) => {
    let key = acht.map((e) => section.secAcheteur.find((x) => x.usr_nom === e)?.usr_cd);

    let removeTask = key.map((element) => {
      return new RequestToSend(
        `Supprimer acheteur ${element}`,
        deleteAcheteur,
        () => { },
        {},
        { plans_seq: plans_seq, usr_cd: element },
        []
      );
    });
    dispatch(addRequestsToSend(removeTask));
  };

  const handleDelete = async () => {
    dispatch(
      addRequestToSend(
        new RequestToSend(
          `Supprimer section ${section.plans_nom}`,
          deleteSection,
          (deletedSection) => {
            if (!deletedSection?.error) {
              handleClose();
            }
          },
          {},
          section,
          []
        )
      )
    );
  };

  const handleDeletes = async (e: any) => {
    setDirty(true);
    let newPerson = buyerNames.filter((x) => x !== e);
    setBuyerNames(newPerson);
  };

  const handleSubmit = async () => {
    let original: string[] = section.secAcheteur.map((e) => e.usr_nom);
    let newList: string[] = buyerNames;

    let removed = original.filter((e) => !newList.includes(e));
    let added = newList.filter((e) => !original.includes(e));

    if (
      sectionName !== section.plans_nom ||
      division?.div_num !== section.div_num ||
      sectionIndex !== section.plans_ordre
    )
      dispatch(
        addRequestToSend(
          new RequestToSend(
            `Modification section ${sectionName}`,
            updateSection,
            () => { },
            {},
            {
              plans_seq: section.plans_seq,
              plans_nom: sectionName,
              div_num: division?.div_num,
              plans_ordre: sectionIndex,
            } as Section,
            []
          )
        )
      );
    await addedAcheteur(section.plans_seq, added);
    await deletedAcheteur(section.plans_seq, removed);
  };

  const handleChange = (event: SelectChangeEvent<typeof buyerNames>) => {
    const {
      target: { value },
    } = event;
    setBuyerNames(typeof value === "string" ? value.split(",") : value);
  };

  return (
    <Box sx={{ display: "flex", alignItems: "center" }}>
      <TextField
        defaultValue={section.plans_nom || ""}
        label="Nom de la section"
        onChange={(e) => {
          setDirty(true);
          setSectionName(e.target.value);
        }}
      />
      <Autocomplete
        options={divisions}
        getOptionLabel={(division: Division) => division.div_nom_aff}
        onInputChange={async (event) => {
          event?.stopPropagation();
        }}
        sx={{
          width: 300,
          m: 1,
          "& input.Mui-disabled": { WebkitTextFillColor: "Black" },
        }}
        onChange={(e, division) => {
          setDirty(true);
          setDivision(division);
        }}
        value={division}
        renderInput={(params) => (
          <TextField
            onClick={(e) => e.stopPropagation()}
            {...params}
            variant="outlined"
            label="Division"
          />
        )}
      />
      <TextField
        defaultValue={section.plans_ordre || null}
        type="number"
        label="Ordre"
        InputProps={{
          inputProps: {
            min: 0,
          },
        }}
        onChange={(e) => {
          setDirty(true);
          setSectionIndex(parseInt(e.target.value));
        }}
      />

      <FormControl sx={{ m: 1, width: 600 }}>
        <InputLabel
          id="demo-multiple-name-label"
          sx={{
            backgroundColor: "#ffffff",
            paddingLeft: "5px",
            paddingRight: "5px",
          }}
        >
          Acheteur
        </InputLabel>
        <Select
          sx={{
            padding: 0,
            maxHeight: 56,
            "& input.Mui-disabled": { WebkitTextFillColor: "Black" },
          }}
          id="demo-multiple-name"
          open={isOpen}
          onClose={(e) => {
            e.stopPropagation();
            setIsOpen(false);
          }}
          onOpen={() => setIsOpen(true)}
          multiple
          value={buyerNames}
          onChange={(e) => {
            e.stopPropagation();
            handleChange(e);
            setDirty(true);
          }}
          MenuProps={MenuProps}
          renderValue={(selected) => (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
              {selected.map((value) => (
                <Chip
                  key={value}
                  label={value}
                  onMouseDown={(e) => e.stopPropagation()}
                  onDelete={(e) => {
                    handleDeletes(value);
                  }}
                  sx={{ WebkitTextFillColor: "black" }}
                />
              ))}
            </Box>
          )}
        >
          {users.map((name) => (
            <MenuItem
              key={name.usr_nom}
              value={name.usr_nom}
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              {name.usr_nom}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <Tooltip title="Enregistrer">
        <span
          style={{
            display: "flex",
            alignItems: "center",
            padding: "6px 28px",
            cursor: "pointer",
          }}
          className={`save-wrapper ${!dirty ? "custom-disabled" : ""}`}
        >
          <span
            className={`k-icon k-font-icon k-i-save k-icon-xl`}
            onClick={handleSubmit}
          ></span>
        </span>
      </Tooltip>
      <Tooltip title="Effacer">
        <span>
          <Button
            color="error"
            disabled={!d_inv026.canDelete}
            onClick={handleDelete}
          >
            <DeleteIcon />
          </Button>
        </span>
      </Tooltip>
    </Box>
  );
}
